import { MinusButton } from "@/components/Buttons/MinusButton";
import { PlusButton } from "@/components/Buttons/PlusButton";
import { useAuthStore } from "@/store/auth.store";
import { cn } from "@roman/shared/utils";
import { useCallback, useEffect, useRef, useState } from "react";
import { useThrottle } from "react-use";

type NumberInputWithControlsProps = {
  value: number;
  setValue: (fn: (prev: number) => number) => void;
  isShowQuickAdd?: boolean;
  throttle?: number;
  setIsKeyboardOpen?: (isOpen: boolean) => void;
};
const QUICK_ADD_VALUES = [1, 20, 100];

// Timing constants
const HOLD_START_DELAY = 100; // ms before auto-repeat starts
const SLOW_INTERVAL = 150; // ms between increments for first phase
const FAST_INTERVAL = 30; // ms between increments for fast phase
const SPEED_UP_THRESHOLD = 2000; // ms after which speed increases

// Step constants
const SLOW_STEP_VALUE = 1;
const FAST_STEP_VALUE = 10;

const NumberInputWithControls = ({
  value,
  setValue,
  isShowQuickAdd = false,
  throttle = 100,
  setIsKeyboardOpen,
}: NumberInputWithControlsProps) => {
  const min = 0;
  const max = 9_999_999;

  const [val, setVal] = useState(value);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const holdStartRef = useRef<number>(0);

  const userPoints = useAuthStore((state) => (state.user?.freePoints ? Number.parseInt(state.user.freePoints) : 0));
  const defaultPoints = userPoints > 0 ? userPoints : 1000; // Default to 1000 if no user points available

  const throttledVal = useThrottle(val, throttle);

  useEffect(() => {
    setValue(() => throttledVal);
  }, [throttledVal, setValue]);

  const clearAllTimers = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const handlePress = useCallback(
    (direction: 1 | -1 = 1) => {
      clearAllTimers();
      holdStartRef.current = Date.now();
      // Only update local state, throttled effect will update parent
      setVal((prev) => {
        const next = prev + direction * SLOW_STEP_VALUE;
        return Math.max(min, Math.min(max, next));
      });

      timeoutRef.current = setTimeout(() => {
        intervalRef.current = setInterval(() => {
          const held = Date.now() - holdStartRef.current;
          let stepValue = SLOW_STEP_VALUE;

          if (held > SPEED_UP_THRESHOLD) {
            stepValue = FAST_STEP_VALUE;

            // Switch to faster interval
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = setInterval(() => {
                setVal((prev) => {
                  const next = prev + direction * FAST_STEP_VALUE;
                  return Math.max(min, Math.min(max, next));
                });
              }, FAST_INTERVAL);
              return;
            }
          }

          setVal((prev) => {
            const next = prev + direction * stepValue;
            return Math.max(min, Math.min(max, next));
          });
        }, SLOW_INTERVAL);
      }, HOLD_START_DELAY);
    },
    [clearAllTimers],
  );

  return (
    <div className="w-full select-none">
      <div className="w-full flex justify-center items-center">
        <div className="flex items-center gap-3 justify-center py-4">
          <MinusButton
            onPointerDown={(e) => {
              if (e.pointerType === "mouse" || e.pointerType === "touch") {
                handlePress(-1);
              }
            }}
            onPointerUp={clearAllTimers}
            onPointerLeave={clearAllTimers}
            className={cn(val === 0 && "bg-disabled cursor-default")}
          />

          <div className="flex items-center gap-1.5 text-2xl font-bold min-w-0">
            <input
              type="text"
              inputMode="numeric"
              value={val.toLocaleString()}
              onChange={(e) => {
                let raw = e.target.value.replace(/[^0-9]/g, "");
                raw = raw.replace(/^0+(?=\d)/, "");
                const num = raw === "" ? 0 : Math.max(0, Math.min(Number(raw), max));
                setVal(() => num);
              }}
              className="text-2xl outline-none p-0 m-0 px-3 py-1 bg-light-blue rounded-lg w-40 text-right focus:ring-1 focus:ring-primary font-bold text-[26px]"
              onFocus={() => {
                if (setIsKeyboardOpen) setIsKeyboardOpen(true);
              }}
              onBlur={() => {
                if (setIsKeyboardOpen) setIsKeyboardOpen(false);
              }}
            />
            <span className="text-xl font-medium">pt</span>
            <span
              className="invisible absolute pointer-events-none text-[26px] font-bold"
              style={{ whiteSpace: "pre" }}
            >
              {val.toLocaleString()}
            </span>
          </div>

          <PlusButton
            onPointerDown={(e) => {
              if (e.pointerType === "mouse" || e.pointerType === "touch") {
                handlePress(1);
              }
            }}
            onPointerUp={clearAllTimers}
            onPointerLeave={clearAllTimers}
            className={cn(val === max && "bg-disabled cursor-default")}
          />
        </div>
      </div>

      {isShowQuickAdd && (
        <div className="flex gap-2 w-full py-4">
          {QUICK_ADD_VALUES.map((v) => (
            <button
              key={v}
              type="button"
              className="flex-1 py-2 rounded bg-gray font-semibold"
              onClick={() => setVal((prev) => prev + v)}
            >
              +{v} pt
            </button>
          ))}
          <button
            className="flex-1 py-2 rounded bg-gray font-semibold"
            onClick={() => setVal(() => defaultPoints)}
            type="button"
          >
            Max
          </button>
        </div>
      )}
    </div>
  );
};

export default NumberInputWithControls;
